import moment from 'moment';
import { Chart, registerables } from 'chart.js';
import HelperMixin from '../../Shared/HelperMixin.vue';

Chart.register(...registerables);

// UI-17 — Detailed Revenue Dashboard.
// Surfaces 4 KPI cards (today's revenue / month revenue / month trips /
// avg ticket size) + 3 charts (daily revenue line, revenue by category
// bar, revenue by payment method pie) with a date-range filter.
//
// Data source: GET /api/admin/Reports/Revenue (ReportsController.GetRevenueReport).
//   Response shape:
//     {
//       kpis: { todayRevenue, monthRevenue, monthTrips, avgTicket,
//               todayDelta, monthDelta, tripsDelta, avgTicketDelta },
//       dailySeries: [{ date: '2026-05-01', revenue: 12345, prevRevenue: 9000 }, ...],
//       categoryBreakdown: [{ key, label, value }, ...],
//       paymentBreakdown:  [{ key, label, value }, ...]
//     }
//
// Audit 2026-07-25: the deterministic mock payload that used to render when
// the call failed has been REMOVED (same call LiveTracking made for LT-07).
// The endpoint exists, so the only remaining trigger was a genuine failure —
// an expired session painted ~260,000 د.ل of invented revenue behind a small
// "بيانات تجريبية" tag. On failure we now show an explicit error state and
// zeroed figures, so admins never read fabricated money as real.

const CATEGORY_COLORS = {
    classic: '#4f46e5',
    luxury: '#0ea5e9',
    women: '#ec4899',
    shared: '#22c55e',
    students: '#f59e0b',
    schools: '#8b5cf6',
};

const PAYMENT_COLORS = {
    wallet: '#4f46e5',
    card: '#0ea5e9',
    cash: '#f59e0b',
};

// Cycling palette used to colour dynamic (backend-driven) category /
// payment slices, since names are not known ahead of time.
const PALETTE = [
    '#4f46e5', '#0ea5e9', '#22c55e', '#f59e0b', '#ec4899',
    '#8b5cf6', '#14b8a6', '#ef4444', '#84cc16', '#f97316',
    '#06b6d4', '#a855f7',
];
const ColorAt = (i) => PALETTE[((i % PALETTE.length) + PALETTE.length) % PALETTE.length];

export default {
    name: 'RevenueDashboard',
    mixins: [HelperMixin],

    async created() {
        this.loginDetails = await this.CheckLoginStatus();
        if (!this.requireUserType([1])) return;
        // Default range: last 30 days.
        this.DateTo = moment().format('YYYY-MM-DD');
        this.DateFrom = moment().subtract(29, 'days').format('YYYY-MM-DD');
        this.ActiveQuick = '30d';
        await this.Load();
    },

    beforeDestroy() {
        this.DestroyCharts();
    },

    data() {
        return {
            DateFrom: '',
            DateTo: '',
            ActiveQuick: '30d',
            LoadError: '',
            LastUpdated: '—',

            Kpis: {
                todayRevenue: 0,
                monthRevenue: 0,
                monthTrips: 0,
                avgTicket: 0,
                todayDelta: 0,
                monthDelta: 0,
                tripsDelta: 0,
                avgTicketDelta: 0,
            },

            DailySeries: [],          // [{ date, revenue, prevRevenue }]
            CategoryBreakdown: [],    // [{ key, label, value, color }]
            PaymentBreakdown: [],     // [{ key, label, value, percent, color }]

            // Chart.js instances (kept off-data so Vue doesn't proxy them).
            _dailyChart: null,
            _categoryChart: null,
            _paymentChart: null,
        };
    },

    methods: {
        // -------- Formatting helpers --------
        FormatLyd(v) {
            const n = Number(v) || 0;
            return n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
        },
        FormatNumber(v) {
            const n = Number(v) || 0;
            return n.toLocaleString('en-US');
        },
        FormatTrend(d) {
            const n = Number(d) || 0;
            const sign = n > 0 ? '+' : '';
            return `${sign}${n.toFixed(1)}%`;
        },
        TrendClass(d) {
            const n = Number(d) || 0;
            if (n > 0.05) return 'trend-up';
            if (n < -0.05) return 'trend-down';
            return 'trend-flat';
        },
        TrendIcon(d) {
            const n = Number(d) || 0;
            if (n > 0.05) return 'solar:arrow-up-line-duotone';
            if (n < -0.05) return 'solar:arrow-down-line-duotone';
            return 'solar:minus-line-duotone';
        },

        // -------- Quick-range buttons --------
        ApplyQuickRange(key) {
            const today = moment();
            if (key === 'today') {
                this.DateFrom = today.format('YYYY-MM-DD');
                this.DateTo = today.format('YYYY-MM-DD');
            } else if (key === '7d') {
                this.DateFrom = today.clone().subtract(6, 'days').format('YYYY-MM-DD');
                this.DateTo = today.format('YYYY-MM-DD');
            } else if (key === '30d') {
                this.DateFrom = today.clone().subtract(29, 'days').format('YYYY-MM-DD');
                this.DateTo = today.format('YYYY-MM-DD');
            } else if (key === 'month') {
                this.DateFrom = today.clone().startOf('month').format('YYYY-MM-DD');
                this.DateTo = today.format('YYYY-MM-DD');
            }
            this.ActiveQuick = key;
            this.Load();
        },

        // -------- Data load --------
        async Load() {
            this.$blockUI && this.$blockUI.Start && this.$blockUI.Start();
            try {
                let payload = null;
                try {
                    if (!this.$http || typeof this.$http.GetRevenueReport !== 'function') {
                        throw new Error('DataService.GetRevenueReport unavailable');
                    }
                    const res = await this.$http.GetRevenueReport(this.DateFrom, this.DateTo);
                    payload = res && res.data ? res.data : null;
                    if (!payload) throw new Error('empty response');
                    this.LoadError = '';
                } catch (e) {
                    // No mock fallback: a failed call means we do NOT know the
                    // revenue, so we say so instead of drawing invented numbers.
                    this.LoadError = 'تعذّر تحميل بيانات الإيرادات — تحقق من الاتصال أو من انتهاء الجلسة ثم أعد المحاولة.';
                    payload = null;
                }

                this.ApplyPayload(payload || {});
                this.LastUpdated = this.LoadError ? '—' : moment().format('YYYY-MM-DD HH:mm');

                // Render after Vue patches DOM (canvas refs become available
                // only once the v-for/loops above settle).
                this.$nextTick(() => this.RenderCharts());
            } finally {
                this.$blockUI && this.$blockUI.Stop && this.$blockUI.Stop();
            }
        },

        ApplyPayload(p) {
            this.Kpis = Object.assign({
                todayRevenue: 0, monthRevenue: 0, monthTrips: 0, avgTicket: 0,
                todayDelta: 0, monthDelta: 0, tripsDelta: 0, avgTicketDelta: 0,
            }, p.kpis || {});

            this.DailySeries = Array.isArray(p.dailySeries) ? p.dailySeries : [];

            // Categories come straight from the backend (dynamic key/label/value).
            // We only attach a colour, cycling a small palette by index so any
            // set of category names renders. Known keys keep their themed colour.
            this.CategoryBreakdown = (Array.isArray(p.categoryBreakdown) ? p.categoryBreakdown : [])
                .map((c, i) => ({
                    key: c.key,
                    label: c.label || c.key,
                    value: Number(c.value) || 0,
                    color: CATEGORY_COLORS[c.key] || ColorAt(i),
                }));

            // Payments: same dynamic treatment, plus a percent for the legend.
            const rawPay = (Array.isArray(p.paymentBreakdown) ? p.paymentBreakdown : [])
                .map((c, i) => ({
                    key: c.key,
                    label: c.label || c.key,
                    value: Number(c.value) || 0,
                    color: PAYMENT_COLORS[c.key] || ColorAt(i),
                }));
            const payTotal = rawPay.reduce((s, x) => s + (Number(x.value) || 0), 0) || 1;
            this.PaymentBreakdown = rawPay.map(x => Object.assign({}, x, {
                percent: ((x.value / payTotal) * 100).toFixed(1),
            }));
        },

        // -------- Chart rendering --------
        DestroyCharts() {
            ['_dailyChart', '_categoryChart', '_paymentChart'].forEach(k => {
                if (this[k]) {
                    try { this[k].destroy(); } catch (e) { /* noop */ }
                    this[k] = null;
                }
            });
        },

        RenderCharts() {
            this.DestroyCharts();
            this.RenderDailyChart();
            this.RenderCategoryChart();
            this.RenderPaymentChart();
        },

        RenderDailyChart() {
            const ctx = this.$refs.dailyChart && this.$refs.dailyChart.getContext('2d');
            if (!ctx) return;
            const labels = this.DailySeries.map(d => moment(d.date).format('DD/MM'));
            const current = this.DailySeries.map(d => Number(d.revenue) || 0);
            const previous = this.DailySeries.map(d => Number(d.prevRevenue) || 0);

            // Gradient fill for "current" line.
            const gradient = ctx.createLinearGradient(0, 0, 0, 240);
            gradient.addColorStop(0, 'rgba(79,70,229,0.35)');
            gradient.addColorStop(1, 'rgba(79,70,229,0.00)');

            this._dailyChart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'الفترة الحالية (د.ل)',
                            data: current,
                            borderColor: '#4f46e5',
                            backgroundColor: gradient,
                            tension: 0.35,
                            fill: true,
                            pointRadius: 2,
                            pointHoverRadius: 5,
                            borderWidth: 2.5,
                        },
                        {
                            label: 'الفترة السابقة (د.ل)',
                            data: previous,
                            borderColor: 'rgba(148,163,184,0.9)',
                            backgroundColor: 'transparent',
                            borderDash: [6, 6],
                            tension: 0.35,
                            fill: false,
                            pointRadius: 0,
                            borderWidth: 2,
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: {
                            position: 'top',
                            align: 'end',
                            labels: { boxWidth: 12, font: { weight: '700' } }
                        },
                        tooltip: {
                            rtl: true,
                            callbacks: {
                                label: (item) => `${item.dataset.label}: ${Number(item.parsed.y).toLocaleString('en-US')} د.ل`
                            }
                        }
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: { font: { weight: '600' }, color: '#64748b' }
                        },
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(15,23,42,0.06)' },
                            ticks: {
                                color: '#64748b',
                                callback: (v) => Number(v).toLocaleString('en-US')
                            }
                        }
                    }
                }
            });
        },

        RenderCategoryChart() {
            const ctx = this.$refs.categoryChart && this.$refs.categoryChart.getContext('2d');
            if (!ctx) return;
            const labels = this.CategoryBreakdown.map(c => c.label);
            const values = this.CategoryBreakdown.map(c => c.value);
            const colors = this.CategoryBreakdown.map(c => c.color);

            this._categoryChart = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [{
                        label: 'الإيرادات (د.ل)',
                        data: values,
                        backgroundColor: colors,
                        borderRadius: 8,
                        borderSkipped: false,
                        maxBarThickness: 42,
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            rtl: true,
                            callbacks: {
                                label: (item) => `${Number(item.parsed.y).toLocaleString('en-US')} د.ل`
                            }
                        }
                    },
                    scales: {
                        x: { grid: { display: false }, ticks: { color: '#64748b', font: { weight: '700' } } },
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(15,23,42,0.06)' },
                            ticks: {
                                color: '#64748b',
                                callback: (v) => Number(v).toLocaleString('en-US')
                            }
                        }
                    }
                }
            });
        },

        RenderPaymentChart() {
            const ctx = this.$refs.paymentChart && this.$refs.paymentChart.getContext('2d');
            if (!ctx) return;
            const labels = this.PaymentBreakdown.map(p => p.label);
            const values = this.PaymentBreakdown.map(p => p.value);
            const colors = this.PaymentBreakdown.map(p => p.color);

            this._paymentChart = new Chart(ctx, {
                type: 'pie',
                data: {
                    labels,
                    datasets: [{
                        data: values,
                        backgroundColor: colors,
                        borderColor: '#fff',
                        borderWidth: 3,
                        hoverOffset: 8,
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            rtl: true,
                            callbacks: {
                                label: (item) => {
                                    const total = item.dataset.data.reduce((s, x) => s + (Number(x) || 0), 0) || 1;
                                    const pct = ((Number(item.parsed) / total) * 100).toFixed(1);
                                    return `${item.label}: ${Number(item.parsed).toLocaleString('en-US')} د.ل (${pct}%)`;
                                }
                            }
                        }
                    }
                }
            });
        },

        // ============ NOTE ============
        // The deterministic mock payload generator (BuildMockPayload) was
        // removed in the 2026-07-25 dashboard-numbers audit. It fabricated
        // ~6,500-10,700 د.ل of daily revenue plus fixed month/trip deltas and
        // rendered them as real whenever the API call threw — including on a
        // merely expired session. The page now surfaces LoadError instead.
    }
};
